import PrivateImage from '../presentation/PrivateImage';
import React, {useState, useEffect} from "react";
import {makeStyles} from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import Title from "../dashboard/Title";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import UserService from "../../services/user.service";
import {logOut} from "../../helpers/logout";
import {NotificationManager} from 'react-notifications';
import Paper from "@material-ui/core/Paper";
import TextInput from "../form/input/TextInput";
import StringArrayInput from "../form/input/StringArrayInput";
import AuthService from "../../services/auth.service";
import {TextField} from "@material-ui/core";
import {PhotoCamera, VpnKey, Sync, Assignment, Receipt} from "@material-ui/icons";
import Popup from "../presentation/Popup";
import ActionButton from "../form/input/ActionButton";
import Tooltip from "@material-ui/core/Tooltip";
import EditIcon from "@material-ui/icons/Edit";
import Grid from "@material-ui/core/Grid";
import {useDashboardStyles, BRAND} from "./dashboardStyles";

const useLocalStyles = makeStyles((theme) => ({
    logoBox: {
        background: BRAND.creamSoft,
        border: `1px solid ${BRAND.goldSoft}`,
        borderRadius: 10,
        padding: theme.spacing(2),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: theme.spacing(2),
        minHeight: 140,
    },
    logoImg: {
        maxHeight: 110,
        maxWidth: "100%",
        objectFit: "contain",
    },
    logoEmpty: {
        color: BRAND.inkSoft,
        fontSize: "0.9rem",
        textAlign: "center",
    },
    uploadLabel: {
        display: "inline-flex",
        alignItems: "center",
        gap: theme.spacing(1),
        padding: theme.spacing(1, 1.75),
        background: BRAND.creamSoft,
        border: `1.5px solid ${BRAND.goldSoft}`,
        borderRadius: 8,
        color: BRAND.deep,
        fontWeight: 600,
        fontSize: "0.9rem",
        cursor: "pointer",
        transition: "background 180ms ease, border-color 180ms ease",
        "&:hover": {
            background: BRAND.goldBg,
            borderColor: BRAND.gold,
        },
        "& svg": {fontSize: 20},
    },
    uploadFilename: {
        marginTop: theme.spacing(1),
        fontSize: "0.85rem",
        color: BRAND.inkSoft,
        fontStyle: "italic",
    },
    passwordHelper: {
        padding: theme.spacing(1.5, 2),
        background: BRAND.goldBg,
        border: `1px solid ${BRAND.goldSoft}`,
        borderRadius: 8,
        color: BRAND.deep,
        fontSize: "0.88rem",
        marginBottom: theme.spacing(2.5),
        lineHeight: 1.5,
    },
    flowiiConnected: {
        color: BRAND.success,
    },
}));

const normalizeProfileValues = (values) => [...new Set((values || [])
    .map((value) => (typeof value === "string" ? value : value?.name || value?.title || "").trim())
    .filter(Boolean))];

export default function ProfileDashboard(props) {
    const classes = useDashboardStyles();
    const localClasses = useLocalStyles();

    const [values, setValues] = useState({
        email: '',
        name: '',
        address: '',
        ico: '',
        ic_dph: '',
        iban: '',
        account_name: '',
        next_invoice_number: 1,
        phone: '',
        logo: '',
        replaceLogo: null,
        email_official: '',
        web: '',
        stamp: '',
        employees: [],
        automobiles: []
    });

    const [editValues, setEditValues] = useState({
        email: '',
        name: '',
        address: '',
        ico: '',
        ic_dph: '',
        iban: '',
        account_name: '',
        next_invoice_number: 1,
        phone: '',
        logo: '',
        replaceLogo: null,
        email_official: '',
        web: '',
        employees: [],
        automobiles: []
    });

    const [password, setPassword] = useState({
        oldPassword: '',
        password: '',
        repeat: '',
    });

    const [flowii, setFlowii] = useState({
        flowii_username: '',
        flowii_password: '',
        flowii_api_key: '',
        flowii_company_id: ''
    });

    const [stamp, setStamp] = useState({
        stamp: '',
        replaceStamp: null,
    });

    const [invoice, setInvoice] = useState({
        iban: '',
        next_invoice_number: 1,
        account_name: '',
    });

    const currentUser = AuthService.getCurrentUser();

    const [show, setShow] = useState(false);
    const [passShow, setPassShow] = useState(false);
    const [flowiiShow, setFlowiiShow] = useState(false);
    const [stampShow, setStampShow] = useState(false);
    const [invoiceShow, setInvoiceShow] = useState(false);

    const handleChange = (event) => {
        const {name, value} = event.target;
        setEditValues({...editValues, [name]: value});
    }

    const handleStringArrayChange = (name, values) => {
        setEditValues({...editValues, [name]: normalizeProfileValues(values)});
    }

    const handlePasswordChange = (event) => {
        const {name, value} = event.target;
        setPassword({...password, [name]: value});
    }

    const handleFlowiiChange = (event) => {
        const {name, value} = event.target;
        setFlowii({...flowii, [name]: value});
    }

    const handleInvoiceChange = (event) => {
        const {name, value} = event.target;
        setInvoice({...invoice, [name]: value});
    }

    useEffect(() => {
        UserService.getUser(currentUser.id).then(
            (response) => {
                setValues({
                    ...response.data,
                    employees: normalizeProfileValues(response.data.employees),
                    automobiles: normalizeProfileValues(response.data.automobiles)
                });
                setFlowii(response.data);
                setInvoice({
                    iban: response.data.iban || '',
                    account_name: response.data.account_name || '',
                    next_invoice_number: response.data.next_invoice_number || 1
                });
            },
            (e) => {
                if (e.response.status === 401) {
                    logOut(props);
                }
            }
        );
    }, [currentUser.id, props]);

    const handleLogo = (e) => {
        setEditValues({...editValues, 'replaceLogo': e.target.files[0]})
    }

    const handleStamp = (e) => {
        setStamp({...stamp, 'replaceStamp': e.target.files[0]})
    }

    const handleCreate = (e) => {
        e.preventDefault();

        if (validate()) {
            const employees = normalizeProfileValues(editValues.employees);
            const automobiles = normalizeProfileValues(editValues.automobiles);
            const data = new FormData();
            data.append('email', editValues.email);
            data.append('name', editValues.name);
            data.append('address', editValues.address);
            data.append('ico', editValues.ico);
            data.append('ic_dph', editValues.ic_dph);
            data.append('next_invoice_number', editValues.next_invoice_number);
            data.append('iban', editValues.iban);
            data.append('account_name', editValues.account_name);
            data.append('phone', editValues.phone);
            data.append('email_official', editValues.email_official);
            data.append('web', editValues.web);
            data.append('employees', JSON.stringify(employees));
            data.append('automobiles', JSON.stringify(automobiles));
            if (editValues.replaceLogo) {
                data.append('someimage', editValues.replaceLogo, editValues.replaceLogo.name);
            }

            UserService.updateUser(data).then(
                (user) => {
                    setValues({
                        ...user.data,
                        employees: normalizeProfileValues(user.data.employees),
                        automobiles: normalizeProfileValues(user.data.automobiles)
                    });
                    setShow(false);
                    NotificationManager.success('Dáta o používateľovi upravené.', 'Upravené', 5000);
                },
                (e) => {
                    if (e.response.status === 401) {
                        logOut(props);
                        NotificationManager.warning('Potrebné sa znova prihlásiť!', 'Upozornenie', 5000);
                    } else {
                        NotificationManager.error('Došlo k neočakávanej chybe. Skúste neskôr alebo kontaktujte podporu.', 'Chyba', 5000);
                    }
                }
            );
        } else {
            NotificationManager.error('Skontrolujte vyplnený formulár!', 'Chyba', 5000);
        }
    };

    const checkOldPass = (code) => {
        return code && code.toString().includes('403');
    }

    const handleChangePassword = (e) => {
        e.preventDefault();

        if (validatePass()) {
            const data = {
                oldPassword: password.oldPassword,
                password: password.password
            }
            UserService.updatePassword(data).then(
                () => {
                    setPassword({
                        oldPassword: '',
                        password: '',
                        repeat: '',
                    });
                    setPassShow(false);
                    NotificationManager.success('Heslo bolo zmenené.', 'Upravené', 5000);
                },
                (e) => {
                    if (checkOldPass(e)) {
                        NotificationManager.error('Zadali ste zlé pôvodné heslo!', 'Chyba', 5000);
                    } else {
                        if (e.response.status === 401) {
                            logOut(props);
                            NotificationManager.error('Musíte sa nanovo prihlásiť.', 'Chyba', 5000);
                        } else {
                            NotificationManager.error('Došlo k neočakávanej chybe. Skúste neskôr alebo kontaktujte podporu.', 'Chyba', 5000);
                        }
                    }
                }
            );
        } else {
            NotificationManager.error('Skontrolujte vyplnený formulár!', 'Chyba', 5000);
        }
    };

    const handleChangeStamp = (e) => {
        e.preventDefault();

        const data = new FormData();
        if (stamp.replaceStamp) {
            data.append('someimage', stamp.replaceStamp, stamp.replaceStamp.name);
        }

        UserService.updateStamp(data).then(
            (user) => {
                setValues(user.data);
                setStampShow(false);
                NotificationManager.success('Pečiatka bola zmenená.', 'Upravené', 5000);
            },
            (e) => {
                if (e.response.status === 401) {
                    logOut(props);
                    NotificationManager.error('Musíte sa nanovo prihlásiť.', 'Chyba', 5000);
                } else {
                    NotificationManager.error('Došlo k neočakávanej chybe. Skúste neskôr alebo kontaktujte podporu.', 'Chyba', 5000);
                }
            }
        );
    };

    const handleChangeFlowii = (e) => {
        e.preventDefault();

        const data = {
            flowii_username: flowii.flowii_username,
            flowii_password: flowii.flowii_password,
            flowii_api_key: flowii.flowii_api_key,
        }

        UserService.updateFlowii(data).then(
            (response) => {
                setFlowii({
                    flowii_username: '',
                    flowii_password: '',
                    flowii_api_key: '',
                    flowii_company_id: response.data.flowii_company_id,
                });
                setFlowiiShow(false);
                NotificationManager.success('Flowii bolo zmenené.', 'Upravené', 5000);
            },
            (e) => {
                if (e.response.status === 401) {
                    logOut(props);
                    NotificationManager.error('Musíte sa nanovo prihlásiť.', 'Chyba', 5000);
                } else {
                    NotificationManager.error('Došlo k neočakávanej chybe. Skúste neskôr alebo kontaktujte podporu.', 'Chyba', 5000);
                }
            }
        );
    };

    const handleChangeInvoice = (e) => {
        e.preventDefault();

        const data = {
            iban: invoice.iban,
            account_name: invoice.account_name,
            next_invoice_number: invoice.next_invoice_number
        }

        UserService.updateInvoiceSettings(data).then(
            (response) => {
                setValues(response.data);
                setInvoiceShow(false);
                NotificationManager.success('Fakturačné údaje boli zmenené.', 'Upravené', 5000);
            },
            (e) => {
                if (e.response?.status === 401) {
                    logOut(props);
                    NotificationManager.error('Musíte sa nanovo prihlásiť.', 'Chyba', 5000);
                } else {
                    NotificationManager.error('Došlo k neočakávanej chybe. Skúste neskôr alebo kontaktujte podporu.', 'Chyba', 5000);
                }
            }
        );
    };

    const [errors, setErrors] = useState({});

    const validate = () => {
        let temp = {};
        let ca_email = [];
        let result = [];
        ca_email = values.email.split(',');
        result = ca_email.filter(item => !(/$^|.+@.+..+/).test(item))
        temp.email = result.length === 0 ? "" : "Email ma zlý formát!"
        setErrors({...temp})

        // eslint-disable-next-line eqeqeq
        return Object.values(temp).every(x => x == "")
    }

    const validatePass = () => {
        let temp = {};
        temp.password = (/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])[0-9a-zA-Z]{8,}$/).test(password.password) ? "" : "minimálne 8 znakov, aspoň 1 veľké písmeno, aspoň 1 malé písmeno, aspoň 1 číslo";
        temp.repeat = password.password === password.repeat ? "" : "Heslá sa nezhodujú!"
        setErrors({...temp})

        // eslint-disable-next-line eqeqeq
        return Object.values(temp).every(x => x == "")
    }

    return (
        <Container maxWidth="lg" className={classes.pageContainer}>
            <Paper className={classes.paper} elevation={0}>
                <div className={classes.sectionHeader}>
                    <div>
                        <Title>Profil pohrebnej služby</Title>
                    </div>
                    <div className={classes.sectionToolbar}>
                        <ActionButton color="primary">
                            <Tooltip title="Pečiatka">
                                <Assignment fontSize="small" onClick={() => setStampShow(true)}/>
                            </Tooltip>
                        </ActionButton>
                        <ActionButton color={flowii.flowii_company_id ? 'primary' : 'secondary'}>
                            <Tooltip title={flowii.flowii_company_id ? "Flowii (pripojené)" : "Flowii"}>
                                <Sync fontSize="small" onClick={() => setFlowiiShow(true)}/>
                            </Tooltip>
                        </ActionButton>
                        <ActionButton color="primary">
                            <Tooltip title="Fakturačné údaje">
                                <Receipt
                                    fontSize="small"
                                    onClick={() => {
                                        setInvoiceShow(true);
                                        setInvoice({
                                            iban: values.iban || '',
                                            next_invoice_number: values.next_invoice_number || 1,
                                            account_name: values.account_name || ''
                                        });
                                    }}
                                />
                            </Tooltip>
                        </ActionButton>
                        <ActionButton color="primary">
                            <Tooltip title="Zmena hesla">
                                <VpnKey fontSize="small" onClick={() => setPassShow(true)}/>
                            </Tooltip>
                        </ActionButton>
                        <ActionButton color="primary">
                            <Tooltip title="Upraviť profil">
                                <EditIcon
                                    fontSize="small"
                                    onClick={() => {
                                        setShow(true);
                                        setEditValues({
                                            ...values,
                                            employees: normalizeProfileValues(values.employees),
                                            automobiles: normalizeProfileValues(values.automobiles)
                                        });
                                    }}
                                />
                            </Tooltip>
                        </ActionButton>
                    </div>
                </div>

                <div className={classes.detailView}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <div className={localClasses.logoBox}>
                                {values.logo ? (
                                    <PrivateImage src={values.logo} alt="logo" className={localClasses.logoImg}/>
                                ) : (
                                    <div className={localClasses.logoEmpty}>Logo zatiaľ nebolo nahrané.</div>
                                )}
                            </div>
                            <TextField
                                id="name"
                                label="Názov pohrebnej služby"
                                multiline
                                value={values.name || ''}
                                InputProps={{readOnly: true}}
                                fullWidth
                            />
                            <TextField
                                id="email_official"
                                label="Email pre komunikáciu"
                                multiline
                                value={values.email_official || ''}
                                InputProps={{readOnly: true}}
                                fullWidth
                            />
                            <TextField
                                id="email"
                                label="Emaily pre zasielanie notifikácií"
                                multiline
                                value={values.email || ''}
                                InputProps={{readOnly: true}}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                id="web"
                                label="Web stránka pohrebnej služby"
                                multiline
                                value={values.web || ''}
                                InputProps={{readOnly: true}}
                                fullWidth
                            />
                            <TextField
                                id="address"
                                label="Adresa pohrebnej služby"
                                multiline
                                value={values.address || ''}
                                InputProps={{readOnly: true}}
                                fullWidth
                            />
                            <TextField
                                id="ico"
                                label="IČO"
                                multiline
                                value={values.ico || ''}
                                InputProps={{readOnly: true}}
                                fullWidth
                            />
                            <TextField
                                id="ic_dph"
                                label="IČ DPH"
                                multiline
                                value={values.ic_dph || ''}
                                InputProps={{readOnly: true}}
                                fullWidth
                            />
                            <TextField
                                id="phone"
                                label="Telefón"
                                multiline
                                value={values.phone || ''}
                                InputProps={{readOnly: true}}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                id="employees"
                                label="Zamestnanci"
                                multiline
                                value={normalizeProfileValues(values.employees).join(', ')}
                                InputProps={{readOnly: true}}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                id="automobiles"
                                label="Automobily"
                                multiline
                                value={normalizeProfileValues(values.automobiles).join(', ')}
                                InputProps={{readOnly: true}}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                </div>

                {/* Edit Profile */}
                <Popup
                    title={'Úprava profilu'}
                    openPopup={show}
                    setOpenPopup={setShow}
                    actions={
                        <Button
                            variant="contained"
                            type="submit"
                            form="profile-edit-form"
                            className={classes.primaryAction}
                        >
                            Uložiť zmeny
                        </Button>
                    }
                >
                    <form id="profile-edit-form" autoComplete='off' onSubmit={handleCreate}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <div className={classes.formField}>
                                    <TextInput label="Názov pohrebnej služby" name="name" value={editValues.name} onChange={handleChange} fullWidth/>
                                </div>
                                <div className={classes.formField}>
                                    <TextInput
                                        label="Emailové adresy (oddeliť čiarkou)"
                                        name="email"
                                        placeholder="abc@gmail.com,def@gmail.com,..."
                                        value={editValues.email}
                                        error={errors.email}
                                        onChange={handleChange}
                                        fullWidth
                                    />
                                </div>
                                <div className={classes.formField}>
                                    <TextInput label="Email na komunikáciu" name="email_official" value={editValues.email_official} onChange={handleChange} fullWidth/>
                                </div>
                                <div className={classes.formField}>
                                    <TextInput label="Webstránka" name="web" value={editValues.web} onChange={handleChange} fullWidth/>
                                </div>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <div className={classes.formField}>
                                    <TextInput label="Adresa pohrebnej služby" name="address" value={editValues.address} onChange={handleChange} fullWidth/>
                                </div>
                                <div className={classes.formField}>
                                    <TextInput label="IČO" name="ico" value={editValues.ico} onChange={handleChange} fullWidth/>
                                </div>
                                <div className={classes.formField}>
                                    <TextInput label="IČ DPH" name="ic_dph" value={editValues.ic_dph} onChange={handleChange} fullWidth/>
                                </div>
                                <div className={classes.formField}>
                                    <TextInput label="Telefónne číslo" name="phone" value={editValues.phone} onChange={handleChange} fullWidth/>
                                </div>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <div className={classes.formField}>
                                    <StringArrayInput
                                        label="Zamestnanci"
                                        name="employees"
                                        value={editValues.employees}
                                        onChange={handleStringArrayChange}
                                        fullWidth
                                    />
                                </div>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <div className={classes.formField}>
                                    <StringArrayInput
                                        label="Automobily"
                                        name="automobiles"
                                        value={editValues.automobiles}
                                        onChange={handleStringArrayChange}
                                        fullWidth
                                    />
                                </div>
                            </Grid>
                        </Grid>

                        <input
                            accept="image/*"
                            style={{display: 'none'}}
                            id="contained-button-logo"
                            multiple
                            type="file"
                            onChange={handleLogo}
                        />
                        <label htmlFor="contained-button-logo" className={localClasses.uploadLabel}>
                            <PhotoCamera/>
                            Vybrať logo
                        </label>
                        {(editValues.replaceLogo && editValues.replaceLogo.name) && (
                            <div className={localClasses.uploadFilename}>{editValues.replaceLogo.name}</div>
                        )}
                    </form>
                </Popup>

                {/* Change Password */}
                <Popup
                    title={'Zmena hesla'}
                    openPopup={passShow}
                    setOpenPopup={setPassShow}
                    additionalWork={() => {
                        setPassword({oldPassword: '', password: '', repeat: ''});
                        setErrors({});
                    }}
                    maxW="sm"
                    actions={
                        <Button
                            variant="contained"
                            type="submit"
                            form="password-form"
                            className={classes.primaryAction}
                        >
                            Zmeniť heslo
                        </Button>
                    }
                >
                    <Typography variant="body2" component="div" className={localClasses.passwordHelper}>
                        Po zabudnutí hesla je ho možné resetovať len za pomoci administrátora!
                        Dôkladne si preto heslo uchovajte.
                    </Typography>
                    <form id="password-form" autoComplete='off' onSubmit={handleChangePassword}>
                        <div className={classes.formField}>
                            <TextInput label="Pôvodné heslo" name="oldPassword" type="password" value={password.oldPassword} onChange={handlePasswordChange} fullWidth/>
                        </div>
                        <div className={classes.formField}>
                            <TextInput
                                label="Heslo"
                                name="password"
                                placeholder="minimálne 8 znakov, 1 veľké, 1 malé, 1 číslo"
                                type="password"
                                value={password.password}
                                error={errors.password}
                                onChange={handlePasswordChange}
                                fullWidth
                            />
                        </div>
                        <div className={classes.formField}>
                            <TextInput label="Zopakovať heslo" name="repeat" type="password" value={password.repeat} error={errors.repeat} onChange={handlePasswordChange} fullWidth/>
                        </div>
                    </form>
                </Popup>

                {/* Change Stamp */}
                <Popup
                    title={'Zmena pečiatky'}
                    openPopup={stampShow}
                    setOpenPopup={setStampShow}
                    additionalWork={() => setErrors({})}
                    maxW="sm"
                    actions={
                        <Button
                            variant="contained"
                            type="submit"
                            form="stamp-form"
                            className={classes.primaryAction}
                        >
                            Uložiť pečiatku
                        </Button>
                    }
                >
                    {values.stamp && (
                        <div className={localClasses.logoBox}>
                            <PrivateImage src={values.stamp} alt="pečiatka" className={localClasses.logoImg}/>
                        </div>
                    )}
                    <form id="stamp-form" autoComplete='off' onSubmit={handleChangeStamp}>
                        <input
                            accept="image/*"
                            style={{display: 'none'}}
                            id="contained-button-stamp"
                            multiple
                            type="file"
                            onChange={handleStamp}
                        />
                        <label htmlFor="contained-button-stamp" className={localClasses.uploadLabel}>
                            <PhotoCamera/>
                            Nahrať pečiatku
                        </label>
                        {(stamp.replaceStamp && stamp.replaceStamp.name) && (
                            <div className={localClasses.uploadFilename}>{stamp.replaceStamp.name}</div>
                        )}
                    </form>
                </Popup>

                {/* Flowii */}
                <Popup
                    title={'Flowii integrácia'}
                    openPopup={flowiiShow}
                    setOpenPopup={setFlowiiShow}
                    additionalWork={() => setErrors({})}
                    maxW="sm"
                    actions={
                        <Button
                            variant="contained"
                            type="submit"
                            form="flowii-form"
                            className={classes.primaryAction}
                        >
                            Uložiť
                        </Button>
                    }
                >
                    <form id="flowii-form" autoComplete='off' onSubmit={handleChangeFlowii}>
                        <div className={classes.formField}>
                            <TextInput label="Prihlasovacie meno" name="flowii_username" type="string" value={flowii.flowii_username} onChange={handleFlowiiChange} fullWidth/>
                        </div>
                        <div className={classes.formField}>
                            <TextInput label="Heslo" name="flowii_password" type="password" value={flowii.flowii_password} onChange={handleFlowiiChange} fullWidth/>
                        </div>
                        <div className={classes.formField}>
                            <TextInput label="API key" name="flowii_api_key" type="string" value={flowii.flowii_api_key} onChange={handleFlowiiChange} fullWidth/>
                        </div>
                    </form>
                </Popup>

                {/* Invoice */}
                <Popup
                    title={'Fakturačné údaje'}
                    openPopup={invoiceShow}
                    setOpenPopup={setInvoiceShow}
                    additionalWork={() => setErrors({})}
                    maxW="sm"
                    actions={
                        <Button
                            variant="contained"
                            type="submit"
                            form="invoice-form"
                            className={classes.primaryAction}
                        >
                            Uložiť
                        </Button>
                    }
                >
                    <form id="invoice-form" autoComplete='off' onSubmit={handleChangeInvoice}>
                        <div className={classes.formField}>
                            <TextInput label="IBAN" name="iban" type="string" value={invoice.iban} onChange={handleInvoiceChange} fullWidth/>
                        </div>
                        <div className={classes.formField}>
                            <TextInput label="Názov účtu" name="account_name" type="string" value={invoice.account_name} onChange={handleInvoiceChange} fullWidth/>
                        </div>
                        <div className={classes.formField}>
                            <TextInput label="Ďalšie číslo faktúry" name="next_invoice_number" type="number" value={invoice.next_invoice_number} onChange={handleInvoiceChange} fullWidth/>
                        </div>
                    </form>
                </Popup>
            </Paper>
        </Container>
    );
}
